import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { addDays, format, startOfDay } from "date-fns"
import { Plus } from "lucide-react"
import { toast } from "sonner"

import {
  useAvailabilitySlots,
  useCollectionsConfig,
  useLocations,
  useRouteInstancesForDate,
  useRoutesPlanned,
  useShiftExceptions,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { useAuth } from "@/contexts/AuthContext"
import { useScrollbarGutter } from "@/hooks/useScrollbarGutter"
import { formatDayLong } from "@/lib/dateFormats"
import { dayOfWeekOf } from "@/lib/plannedRouteMapping"
import {
  SEGMENT_LINE_STYLE,
  type SegmentLineStyle,
  segmentLoadStates,
} from "@/lib/routeCostCalculation"
import { coverBadge } from "@/lib/shiftCover"
import { selectUnscheduledSlots } from "@/lib/slotCoverage"
import {
  buildHourMarkers,
  DEFAULT_HOUR_WIDTH,
  MAX_HOUR_WIDTH,
  MIN_HOUR_WIDTH,
  ROW_HEIGHT,
  timelineWidthFor,
  timeToPosition,
  ZOOM_STEP,
} from "@/lib/timelineScale"
import type { RouteInstance } from "@/types/collections"

import PlanningRouteMapPanel from "./PlanningRouteMapPanel"
import ShiftExceptionsPanel from "./ShiftExceptionsPanel"
import UnscheduledSlotsPanel from "./UnscheduledSlotsPanel"
import { NewStopDialog } from "./timeline/NewStopDialog"
import { PlanningRouteDialog } from "./timeline/PlanningRouteDialog"
import { PlanningRouteListItem } from "./timeline/PlanningRouteListItem"
import { PlanningTimelineRow } from "./timeline/PlanningTimelineRow"
import { PlanningTimelineToolbar } from "./timeline/PlanningTimelineToolbar"
import { RouteCostPreview } from "./timeline/RouteCostPreview"
import { RouteLoadFactorCard } from "./timeline/RouteLoadFactorCard"
import { SaveScopeDialog } from "./timeline/SaveScopeDialog"
import { TimelineSummaryBar } from "./timeline/TimelineSummaryBar"
import type { InstanceEditMode } from "./timeline/types"
import { UnsavedChangesBanner } from "./timeline/UnsavedChangesBanner"
import { useNewStopDialog } from "./timeline/useNewStopDialog"
import { usePlannedRoutePersistence } from "./timeline/usePlannedRoutePersistence"
import { usePlannedRoutesForDate } from "./timeline/usePlannedRoutesForDate"
import { usePlanningRouteDialog } from "./timeline/usePlanningRouteDialog"
import { useRouteMetrics } from "./timeline/useRouteMetrics"
import { useTimelineDrag } from "./timeline/useTimelineDrag"
import { useVisibleRoutes } from "./timeline/useVisibleRoutes"
import { useVolumeForecasts } from "./timeline/useVolumeForecasts"

/** One row per route the date runs; edits stay local until saved, then apply to this day or all future routes. */
interface PlanningTimelineViewProps {
  editNavigation?: { date: Date; routeId: string; editMode?: "instance" | "planned" } | null
  onClearEditNavigation?: () => void
}

const PlanningTimelineView: React.FC<PlanningTimelineViewProps> = ({
  editNavigation,
  onClearEditNavigation,
}) => {
  const { can, user, isPartner } = useAuth()
  const readOnly = !can.editRoutes
  const tomorrow = addDays(startOfDay(new Date()), 1)
  const [selectedDate, setSelectedDate] = useState<Date>(tomorrow)
  const [highlightedRouteId, setHighlightedRouteId] = useState<string | null>(null)
  const highlightTimerRef = useRef<number | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterEmptyLegOnly, setFilterEmptyLegOnly] = useState(false)
  /** Stand-by routes are part of the day's plan and cost, so shown by default. */
  const [showStandbyRoutes, setShowStandbyRoutes] = useState(true)
  const [filterHaulageApplicable, setFilterHaulageApplicable] = useState(false)
  const [hourWidth, setHourWidth] = useState(DEFAULT_HOUR_WIDTH)
  const [instanceEditMode, setInstanceEditMode] = useState<InstanceEditMode>(null)
  const [showUnscheduledPanel, setShowUnscheduledPanel] = useState(false)
  const [showShiftPanel, setShowShiftPanel] = useState(false)

  const { data: routesPlannedData, refetch: refetchRoutes } = useRoutesPlanned()
  const { data: locationsData } = useLocations()
  const { data: slotsData } = useAvailabilitySlots()
  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()

  const selectedDateStr = format(selectedDate, "yyyy-MM-dd")
  const { data: allRouteInstancesData, refetch: refetchInstances } =
    useRouteInstancesForDate(selectedDateStr)
  // Drop cancelled instances: a regenerated day can hold a cancelled copy, and lookups below take the first match.
  const routeInstancesData = useMemo(
    () => allRouteInstancesData?.filter((ri: RouteInstance) => ri.status !== "cancelled"),
    [allRouteInstancesData],
  )

  const {
    routes,
    setRoutes,
    originalRoutes,
    setOriginalRoutes,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    initialTemplateRoutesRef,
    editRoutes,
    deleteStop,
    updateStopMoney,
  } = usePlannedRoutesForDate({
    routesPlannedData,
    routeInstances: routeInstancesData,
    selectedDate,
    instanceEditMode,
  })

  const locations = useMemo(() => locationsData?.data || [], [locationsData])
  const slots = useMemo(() => slotsData?.data || [], [slotsData])

  const getLocationById = useCallback(
    (locationId: string) => {
      return locations.find((l) => l.id === locationId)
    },
    [locations],
  )

  const { getVolumeForecastForStop, getVolumeForecast } = useVolumeForecasts(
    routeInstancesData,
    selectedDate,
  )

  const {
    newStopDialogOpen,
    setNewStopDialogOpen,
    pendingStopData,
    newStopFormData,
    setNewStopFormData,
    locationSearchTerm,
    setLocationSearchTerm,
    openNewStopDialog,
    handleCreateStop,
  } = useNewStopDialog({ routes, selectedDate, getLocationById, editRoutes })

  const [routeForMapPanelId, setRouteForMapPanelId] = useState<string | null>(null)

  const timelineHeaderRef = useRef<HTMLDivElement>(null)
  const timelineContentRef = useRef<HTMLDivElement>(null)
  const routeListRef = useRef<HTMLDivElement>(null)
  const isScrollingSyncRef = useRef(false)

  // Only the tracks scroll horizontally, so the followers reserve the same scrollbar gutter.
  const scrollbarGutter = useScrollbarGutter(timelineContentRef)

  const { routesForDate, filteredRoutes, firstStandbyRowIndex, dayOperator } = useVisibleRoutes({
    routes,
    selectedDate,
    routeInstances: routeInstancesData,
    searchTerm,
    filterEmptyLegOnly,
    filterHaulageApplicable,
    showStandbyRoutes,
    config: configData?.data,
    getVolumeForecastForStop,
    operatorScope: isPartner ? user?.operator : null,
  })

  // The route may be filtered out of view, so clear the search to make the click visible.
  const highlightRoute = useCallback(
    (routeId: string) => {
      if (searchTerm && !filteredRoutes.some((r) => r.id === routeId)) {
        setSearchTerm("")
      }
      setHighlightedRouteId(routeId)
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current)
      }
      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightedRouteId(null)
        highlightTimerRef.current = null
      }, 3000)
    },
    [searchTerm, filteredRoutes],
  )

  useEffect(
    () => () => {
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current)
      }
    },
    [],
  )

  const { routeCostsMap, routeLoadFactors } = useRouteMetrics({
    filteredRoutes,
    vehicleTypes: vehicleTypesData?.data,
    config: configData?.data,
    locations: locationsData?.data,
    routeInstances: routeInstancesData,
    selectedDate,
    getVolumeForecastForStop,
  })

  // Same query key as the panel, so the badge and the list it opens share one fetch.
  const shiftCoverDate = format(selectedDate, "yyyy-MM-dd")
  const { data: shiftExceptions } = useShiftExceptions({
    from: shiftCoverDate,
    to: shiftCoverDate,
  })
  const shiftCoverBadge = coverBadge(shiftExceptions?.exceptions)

  const unscheduledSlotsCount = useMemo(
    () =>
      selectUnscheduledSlots(slots, routesForDate, dayOfWeekOf(selectedDate)).length,
    [selectedDate, routesForDate, slots],
  )

  const routeForMapPanel = useMemo(
    () => (routeForMapPanelId ? (routes.find((r) => r.id === routeForMapPanelId) ?? null) : null),
    [routeForMapPanelId, routes],
  )
  const mapSegmentStyles = useMemo((): SegmentLineStyle[] => {
    if (!routeForMapPanel?.stops?.length) return []
    const stops = [...routeForMapPanel.stops].sort((a, b) => a.sequence - b.sequence)
    return segmentLoadStates(
      stops,
      (i) => getVolumeForecastForStop(routeForMapPanel, stops[i]) || 0,
    ).map((state) => SEGMENT_LINE_STYLE[state])
  }, [routeForMapPanel, getVolumeForecastForStop])

  const handleTimelineScroll = () => {
    if (isScrollingSyncRef.current) return
    isScrollingSyncRef.current = true

    if (timelineHeaderRef.current && timelineContentRef.current) {
      timelineHeaderRef.current.scrollLeft = timelineContentRef.current.scrollLeft
    }
    if (routeListRef.current && timelineContentRef.current) {
      routeListRef.current.scrollTop = timelineContentRef.current.scrollTop
    }

    requestAnimationFrame(() => {
      isScrollingSyncRef.current = false
    })
  }

  const handleRouteListScroll = () => {
    if (isScrollingSyncRef.current) return
    isScrollingSyncRef.current = true

    if (routeListRef.current && timelineContentRef.current) {
      timelineContentRef.current.scrollTop = routeListRef.current.scrollTop
    }

    requestAnimationFrame(() => {
      isScrollingSyncRef.current = false
    })
  }

  useEffect(() => {
    if (editNavigation) {
      setSelectedDate(editNavigation.date)
      setHighlightedRouteId(editNavigation.routeId)

      if (editNavigation.editMode === "instance") {
        setInstanceEditMode({ routeId: editNavigation.routeId, date: editNavigation.date })
        toast.success("Editing Instance", {
          description: `Changes will only apply to ${formatDayLong(editNavigation.date)}. To change all future occurrences, use "Edit Planned Route".`,
        })
      } else {
        setInstanceEditMode(null)
      }

      if (onClearEditNavigation) {
        onClearEditNavigation()
      }
      const timer = setTimeout(() => {
        setHighlightedRouteId(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [editNavigation, onClearEditNavigation])

  const getTimePosition = useCallback(
    (time: Date) => timeToPosition(time, selectedDate, hourWidth),
    [selectedDate, hourWidth],
  )

  const {
    dragCreateState,
    dragState,
    dropTargetRowIndex,
    handleDragStart,
    handleSlotDragOver,
    handleSlotDragLeave,
    handleSlotDrop,
    handleStopDragStart,
    resetStopDrag,
  } = useTimelineDrag({
    timelineContentRef,
    selectedDate,
    hourWidth,
    routes,
    filteredRoutes,
    editRoutes,
    openNewStopDialog,
  })

  const {
    isSaving,
    setIsSaving,
    saveConfirmPending,
    setSaveConfirmPending,
    handleSaveChanges,
    handleDiscardChanges,
    applyToThisDayOnly,
    applyToAllFuture,
  } = usePlannedRoutePersistence({
    routes,
    setRoutes,
    originalRoutes,
    setOriginalRoutes,
    setHasUnsavedChanges,
    initialTemplateRoutesRef,
    instanceEditMode,
    setInstanceEditMode,
    selectedDate,
    routeInstances: routeInstancesData,
    getLocationById,
    refetchRoutes,
    refetchInstances,
    resetStopDrag,
  })

  const {
    editRouteDialogOpen,
    setEditRouteDialogOpen,
    selectedRouteForEdit,
    routeFormData,
    setRouteFormData,
    isCreatingNewRoute,
    isRenamingRoute,
    effectiveRenameFrom,
    setRenameFromDate,
    routeCodeClash,
    vehicleTypeOptions,
    openEditRouteDialog,
    openCreateRouteDialog,
    handleSaveRoute,
  } = usePlanningRouteDialog({
    routesPlannedData,
    vehicleTypes: vehicleTypesData?.data,
    routes,
    setRoutes,
    originalRoutes,
    setOriginalRoutes,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    selectedDate,
    setInstanceEditMode,
    setIsSaving,
    refetchRoutes,
    refetchInstances,
  })

  const handleAddRoute = useCallback(() => {
    openCreateRouteDialog()
  }, [openCreateRouteDialog])

  const handleZoomIn = () => {
    setHourWidth((prev) => Math.min(prev + ZOOM_STEP, MAX_HOUR_WIDTH))
  }

  const handleZoomOut = () => {
    setHourWidth((prev) => Math.max(prev - ZOOM_STEP, MIN_HOUR_WIDTH))
  }

  const hourMarkers = buildHourMarkers(hourWidth)
  const timelineWidth = timelineWidthFor(hourWidth)
  const rowCount = filteredRoutes.length + (readOnly ? 0 : 1)

  return (
    <div className="flex-1 flex min-h-0 bg-palette-jungleGreen000">
      <div className="flex-1 flex min-h-0 min-w-0">
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {hasUnsavedChanges && (
            <UnsavedChangesBanner
              isSaving={isSaving}
              onDiscard={handleDiscardChanges}
              onSave={handleSaveChanges}
            />
          )}

          <TimelineSummaryBar
            routes={filteredRoutes}
            routeCosts={routeCostsMap}
            showCost={can.costs}
            getVolumeForecastForStop={getVolumeForecastForStop}
          />

          <PlanningTimelineToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterHaulageApplicable={filterHaulageApplicable}
            onFilterHaulageApplicableChange={setFilterHaulageApplicable}
            filterEmptyLegOnly={filterEmptyLegOnly}
            onFilterEmptyLegOnlyChange={setFilterEmptyLegOnly}
            showStandbyRoutes={showStandbyRoutes}
            onShowStandbyRoutesChange={setShowStandbyRoutes}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            minDate={tomorrow}
            hourWidth={hourWidth}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onAddRoute={handleAddRoute}
            showUnscheduledPanel={showUnscheduledPanel}
            onToggleUnscheduledPanel={() => setShowUnscheduledPanel(!showUnscheduledPanel)}
            unscheduledSlotsCount={unscheduledSlotsCount}
            showShiftPanel={showShiftPanel}
            onToggleShiftPanel={() => setShowShiftPanel(!showShiftPanel)}
            shiftCoverBadge={shiftCoverBadge}
            canEditRoutes={!readOnly}
            canSeeShiftCover={can.shiftCover}
            canFilter={can.planFilters}
          />

          {!readOnly && (
            <div className="bg-blue-50 border-b border-blue-100 px-4 py-1.5 flex-shrink-0">
              <p className="text-xs text-blue-700">
                💡 <span className="font-medium">Tip:</span> Click and drag on the timeline to
                create a new stop. Drag edges to resize existing stops.
              </p>
            </div>
          )}

          <div className="bg-palette-jungleGreen000 border-b border-palette-jungleGreen200 flex-shrink-0 flex">
            <div className="w-64 h-10 flex items-center px-3 border-r border-palette-jungleGreen200 flex-shrink-0">
              <span className="text-xs font-medium text-palette-jungleGreen500 uppercase tracking-wider">
                Planned Routes
              </span>
            </div>

            <div ref={timelineHeaderRef} className="flex-1 h-10 overflow-x-hidden">
              <div
                className="h-full relative"
                style={{ width: `${timelineWidth + scrollbarGutter.right}px` }}
              >
                {hourMarkers.map((marker) => (
                  <div
                    key={marker.hour}
                    className="absolute top-0 h-full flex flex-col items-center justify-center border-l border-palette-jungleGreen200"
                    style={{ left: `${marker.position}px` }}
                  >
                    <span className="text-xs text-palette-jungleGreen500 font-medium px-2">
                      {marker.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-1 flex min-h-0 overflow-hidden">
            <div
              ref={routeListRef}
              className="w-64 bg-white border-r border-palette-jungleGreen200 flex-shrink-0 overflow-y-auto overflow-x-hidden"
              onScroll={handleRouteListScroll}
            >
              {filteredRoutes.length === 0 ? (
                <div className="p-4 text-center text-palette-jungleGreen500 text-sm">
                  No routes planned for this date
                </div>
              ) : (
                filteredRoutes.map((route, rowIndex) => (
                  <PlanningRouteListItem
                    key={route.id}
                    route={route}
                    operator={dayOperator(route)}
                    isFirstStandby={rowIndex === firstStandbyRowIndex}
                    isHighlighted={highlightedRouteId === route.id}
                    cost={routeCostsMap.get(route.id)}
                    loadFactor={routeLoadFactors.get(route.id)}
                    onEdit={readOnly ? undefined : openEditRouteDialog}
                    onShowMap={setRouteForMapPanelId}
                  />
                ))
              )}

              {!readOnly && (
                <div
                  className="px-3 py-3 border-b border-palette-jungleGreen100 border-dashed cursor-pointer hover:bg-palette-jungleGreen000 transition-colors"
                  onClick={handleAddRoute}
                  style={{ height: `${ROW_HEIGHT}px` }}
                >
                  <div className="flex items-center justify-center h-full text-palette-jungleGreen400 text-sm gap-2">
                    <Plus size={14} />
                    Add new route
                  </div>
                </div>
              )}

              {/* Matches the tracks' horizontal scrollbar so both panes scroll equally far. */}
              {scrollbarGutter.bottom > 0 && (
                <div aria-hidden style={{ height: `${scrollbarGutter.bottom}px` }} />
              )}
            </div>

            <div
              ref={timelineContentRef}
              className="flex-1 overflow-auto"
              onScroll={handleTimelineScroll}
            >
              <div className="relative bg-white" style={{ width: `${timelineWidth}px` }}>
                <div
                  className="absolute w-0.5 bg-amber-400 z-10"
                  style={{
                    left: `${24 * hourWidth}px`,
                    top: 0,
                    height: `${rowCount * ROW_HEIGHT}px`,
                  }}
                />

                {hourMarkers.map((marker) => (
                  <div
                    key={`grid-${marker.hour}`}
                    className="absolute w-px bg-palette-jungleGreen100"
                    style={{
                      left: `${marker.position}px`,
                      top: 0,
                      height: `${rowCount * ROW_HEIGHT}px`,
                    }}
                  />
                ))}

                {filteredRoutes.map((route, rowIndex) => (
                  <PlanningTimelineRow
                    key={route.id}
                    route={route}
                    rowIndex={rowIndex}
                    isFirstStandby={rowIndex === firstStandbyRowIndex}
                    isHighlighted={highlightedRouteId === route.id}
                    isDropTarget={dropTargetRowIndex === rowIndex}
                    readOnly={readOnly}
                    dragCreatePreview={
                      dragCreateState && dragCreateState.rowIndex === rowIndex
                        ? dragCreateState
                        : null
                    }
                    filterHaulageApplicable={filterHaulageApplicable}
                    driverMaxHours={configData?.data?.driver_max_hours_per_day ?? 12}
                    selectedDate={selectedDate}
                    draggingStopId={dragState?.stopId}
                    getTimePosition={getTimePosition}
                    getVolumeForecastForStop={getVolumeForecastForStop}
                    onDragStart={handleDragStart}
                    onSlotDragOver={handleSlotDragOver}
                    onSlotDragLeave={handleSlotDragLeave}
                    onSlotDrop={handleSlotDrop}
                    onStopDragStart={handleStopDragStart}
                    onDeleteStop={deleteStop}
                    onUpdateStopMoney={updateStopMoney}
                  />
                ))}

                {!readOnly && (
                  <div
                    className="relative border-b border-dashed border-palette-jungleGreen200 bg-palette-jungleGreen000/30"
                    style={{ height: `${ROW_HEIGHT}px` }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center text-palette-jungleGreen400 text-xs">
                      Click "New Route" to add a route
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <NewStopDialog
            open={newStopDialogOpen}
            onOpenChange={setNewStopDialogOpen}
            pending={pendingStopData}
            form={newStopFormData}
            setForm={setNewStopFormData}
            locations={locations}
            locationSearchTerm={locationSearchTerm}
            onLocationSearchChange={setLocationSearchTerm}
            onCreate={handleCreateStop}
          />

          <PlanningRouteDialog
            open={editRouteDialogOpen}
            onOpenChange={setEditRouteDialogOpen}
            isCreatingNewRoute={isCreatingNewRoute}
            route={selectedRouteForEdit}
            selectedDate={selectedDate}
            minDate={tomorrow}
            form={routeFormData}
            setForm={setRouteFormData}
            routeCodeClash={routeCodeClash}
            isRenamingRoute={isRenamingRoute}
            effectiveRenameFrom={effectiveRenameFrom}
            onRenameFromChange={setRenameFromDate}
            vehicleTypeOptions={vehicleTypeOptions}
            isSaving={isSaving}
            onSave={() => void handleSaveRoute()}
          >
            <RouteCostPreview
              route={selectedRouteForEdit}
              isCreatingNewRoute={isCreatingNewRoute}
              form={routeFormData}
              selectedDate={selectedDate}
              vehicleTypes={vehicleTypesData?.data}
              config={configData?.data}
              routeInstances={routeInstancesData}
            />

            <RouteLoadFactorCard
              route={selectedRouteForEdit}
              vehicleTypeName={routeFormData.vehicleType}
              vehicleTypes={vehicleTypesData?.data}
              locations={locationsData?.data}
              getVolumeForecastForStop={getVolumeForecastForStop}
              getVolumeForecast={getVolumeForecast}
            />
          </PlanningRouteDialog>

          <SaveScopeDialog
            pending={saveConfirmPending}
            onDismiss={() => setSaveConfirmPending(null)}
            onThisDayOnly={applyToThisDayOnly}
            onAllFuture={applyToAllFuture}
          />
        </div>

        {routeForMapPanelId && (
          <div className="w-[420px] flex-shrink-0 flex flex-col min-h-0 border-l border-palette-jungleGreen200 bg-white overflow-hidden">
            <PlanningRouteMapPanel
              route={routeForMapPanel}
              segmentStyles={mapSegmentStyles}
              onClose={() => setRouteForMapPanelId(null)}
              inline
            />
          </div>
        )}
      </div>

      {can.shiftCover && (
        <ShiftExceptionsPanel
          isOpen={showShiftPanel}
          onClose={() => setShowShiftPanel(false)}
          selectedDate={selectedDate}
          onHighlightRoute={highlightRoute}
        />
      )}

      {!readOnly && (
        <UnscheduledSlotsPanel
          isOpen={showUnscheduledPanel}
          onClose={() => setShowUnscheduledPanel(false)}
          selectedDate={selectedDate}
          slots={slots}
          locations={locations}
          routes={routesForDate}
          onHighlightRoute={highlightRoute}
        />
      )}
    </div>
  )
}

export default PlanningTimelineView
